import React, { useState } from "react";
import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Comment } from "../../types";
import { useAuthStore } from "../../store/authStore";
import { CommentTabs } from "./Comments/CommentTabs";
import { CommentList } from "./Comments/CommentList";
import { CommentDetail } from "./Comments/CommentDetail";
import { cn } from "../../utils/cn";

interface CommentSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  comments: Comment[];
  isLoading: boolean;
  error: string | null;
  onReply: (commentId: string, text: string) => Promise<void>;
  onResolve: (commentId: string) => Promise<void>;
  onDelete: (commentId: string) => Promise<void>;
  onSignIn: () => void;
}

export const CommentSidebar: React.FC<CommentSidebarProps> = ({
  isOpen,
  onClose,
  comments,
  isLoading,
  error,
  onReply,
  onResolve,
  onDelete,
  onSignIn,
}) => {
  const { user, isGuest } = useAuthStore();
  const [activeTab, setActiveTab] = useState<"active" | "resolved">("active");
  const [selectedComment, setSelectedComment] = useState<Comment | null>(null);

  const activeComments = comments.filter((c) => c.status === "active");
  const resolvedComments = comments.filter((c) => c.status === "resolved");

  const handleCommentClick = (comment: Comment) => {
    setSelectedComment(comment);
  };

  const renderContent = () => {
    if (!user && !isGuest && comments.length > 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-6 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Please Sign In to Add Comments
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Sign in to join the discussion and add your comments.
          </p>
          <button
            onClick={onSignIn}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm bg-indigo-600 hover:bg-indigo-700"
            style={{ color: "#ffffff" }}
          >
            Sign In
          </button>
        </div>
      );
    }

    if (comments.length === 0 && user) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-6 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Add Your First Comment
          </h3>
          <p className="text-sm text-gray-600">
            Click anywhere on the page to add your first comment.
          </p>
        </div>
      );
    }

    if (selectedComment) {
      return (
        <CommentDetail
          comment={selectedComment}
          onBack={() => setSelectedComment(null)}
          onResolve={onResolve}
          onDelete={onDelete}
          onReply={onReply}
        />
      );
    }

    return (
      <>
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <CommentTabs
            activeTab={activeTab}
            activeCount={activeComments.length}
            resolvedCount={resolvedComments.length}
            onTabChange={setActiveTab}
          />
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            title="Close sidebar"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <CommentList
            comments={comments}
            activeTab={activeTab}
            onCommentClick={handleCommentClick}
          />
        </div>
      </>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      style={{ zIndex: 2147483648 }} // Higher than everything else
    >
      <div
        className="feedbackflow-sidebar fixed inset-y-4 right-4 w-[320px]"
        style={{ zIndex: 2147483648 }}
      >
        <Dialog.Panel
          className="h-full bg-white rounded-2xl shadow-lg flex flex-col overflow-hidden border border-gray-200"
          style={{ zIndex: 2147483648 }}
        >
          {renderContent()}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};
